import { TokenRequestError } from '@pn/core/services/authentication/tokenRequestError';
import { isCordova } from '@pn/core/utils/env';
import { hasKeyWithType } from '@pn/core/utils/logic';
import { isNumber, isObject, isString } from 'lodash-es';

const clientId = import.meta.env.VITE_APP_AUTH0_CLIENT_ID;
const audience = import.meta.env.VITE_APP_AUTH0_AUDIENCE;

/**
 * Errors after which the stored session cannot produce a token without the
 * user logging in again.
 */
const TERMINAL_CODES = new Set([
  'login_required',
  'consent_required',
  'interaction_required',
  'missing_refresh_token',
  'invalid_grant',
]);

type CacheEntrySummary = {
  audience: string;
  scope: string;
  expiresAt: string;
  hasRefreshToken: boolean;
};

/**
 * Wraps an Auth0 token request so failures surface as a
 * {@link TokenRequestError} carrying a readable message and diagnostics.
 */
export async function requestAuth0Token(
  getAccessTokenSilently: () => Promise<string>
): Promise<string> {
  /* Taken up front since Auth0 may remove entries while handling a failure */
  const cache = summarizeCache();

  try {
    return await getAccessTokenSilently();
  } catch (error) {
    const code = getErrorCode(error);

    throw new TokenRequestError(
      code,
      getErrorDescription(error),
      cache.some((entry) => entry.audience === audience),
      TERMINAL_CODES.has(code),
      {
        platform: isCordova() ? 'cordova' : 'web',
        online: navigator.onLine,
        cache,
      }
    );
  }
}

function getErrorCode(error: unknown): string {
  if (isObject(error) && hasKeyWithType(error, 'error', isString)) {
    return error.error;
  }
  /* Auth0 rethrows `fetch` rejections as is, which are TypeErrors */
  if (error instanceof TypeError) return 'network';

  return 'unknown';
}

function getErrorDescription(error: unknown): string {
  if (
    isObject(error) &&
    hasKeyWithType(error, 'error_description', isString) &&
    error.error_description !== ''
  ) {
    return error.error_description;
  }
  if (error instanceof Error) return error.message;

  return String(error);
}

/**
 * Summarizes the Auth0 localStorage cache without exposing any token values.
 * Keys look like `@@auth0spajs@@::<clientId>::<audience>::<scope>`.
 */
function summarizeCache(): CacheEntrySummary[] {
  const prefix = `@@auth0spajs@@::${clientId}::`;
  const entries: CacheEntrySummary[] = [];

  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key?.startsWith(prefix) || key.endsWith('::@@user@@')) continue;

      const [entryAudience = '', scope = ''] = key
        .slice(prefix.length)
        .split('::');
      const entry: unknown = JSON.parse(localStorage.getItem(key) ?? '{}');

      entries.push({
        audience: entryAudience,
        scope,
        expiresAt:
          isObject(entry) && hasKeyWithType(entry, 'expiresAt', isNumber)
            ? new Date(entry.expiresAt * 1000).toISOString()
            : 'unknown',
        hasRefreshToken:
          isObject(entry) &&
          hasKeyWithType(entry, 'body', isObject) &&
          hasKeyWithType(entry.body, 'refresh_token', isString),
      });
    }
  } catch {
    /* Storage can be unavailable or hold malformed entries */
  }

  return entries;
}
